import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { inc, dec } from '../utils/loadingState';
import { msalInstance } from '../auth/msalInstance';
import { loginRequest } from '../auth/msalConfig';
import { extractErrorMessage } from '../utils/errorMessage';

export const DEFAULT_API_BASE = 'https://localhost:7017';
export const DEFAULT_API_ROOT_SUFFIX = '/api/space';

export const API_BASE = process.env.REACT_APP_API_BASE ?? DEFAULT_API_BASE;
export const API_ROOT = process.env.REACT_APP_API_ROOT ?? `${API_BASE}${DEFAULT_API_ROOT_SUFFIX}`;

// localStorage keys
export const TIFOXR_TOKEN_KEY         = 'tifoxr_token';
export const TIFOXR_TOKEN_EXPIRY_KEY  = 'tifoxr_token_expiry';
export const TIFOXR_SYSTEM_ROLE_KEY   = 'tifoxr_system_role';
export const TIFOXR_ENTITY_ID_KEY     = 'tifoxr_entity_id';
export const TIFOXR_USER_ID_KEY       = 'tifoxr_user_id';
export const TIFOXR_USER_NAME_KEY     = 'tifoxr_name';
export const TIFOXR_USER_EMAIL_KEY    = 'tifoxr_email';
export const TIFOXR_SPACES_KEY        = 'tifoxr_spaces';
export const TIFOXR_ACTIVE_SPACE_KEY  = 'tifoxr_active_space';

export const ALL_STORAGE_KEYS = [
  TIFOXR_TOKEN_KEY,
  TIFOXR_TOKEN_EXPIRY_KEY,
  TIFOXR_SYSTEM_ROLE_KEY,
  TIFOXR_ENTITY_ID_KEY,
  TIFOXR_USER_ID_KEY,
  TIFOXR_USER_NAME_KEY,
  TIFOXR_USER_EMAIL_KEY,
  TIFOXR_SPACES_KEY,
  TIFOXR_ACTIVE_SPACE_KEY,
];


// One-shot key carrying a re-login reason across forceReLogin's navigation (mirrors Login.jsx's msal_redirect_error).
export const TIFOXR_AUTH_ERROR_KEY = 'tifoxr_auth_error';

// classifyAuthFailure's outcomes; exported as constants so a typo can't silently desync comparisons.
export const REAUTH_REQUIRED = 'REAUTH_REQUIRED';
export const NOT_AUTHORIZED  = 'NOT_AUTHORIZED';
export const TRANSIENT       = 'TRANSIENT';

const FRIENDLY_REAUTH_MESSAGE         = 'Your Microsoft sign-in session has expired. Please sign in again.';
const FRIENDLY_NOT_AUTHORIZED_MESSAGE = 'You do not have access to the CMS. Contact your administrator.';
const FRIENDLY_TRANSIENT_MESSAGE      = 'Could not reach the server. Please check your connection and try again.';

// Only `instanceof InteractionRequiredAuthError` means reauth is needed -- other MSAL errors (e.g. a network blip) are transient.
export function classifyAuthFailure(err) {
  if (err instanceof InteractionRequiredAuthError) {
    return { kind: REAUTH_REQUIRED, message: FRIENDLY_REAUTH_MESSAGE };
  }

  const status = err?.response?.status;
  if (status === 401) {
    // Backend rejected the credential itself; retrying with the same cached MSAL data won't help.
    return { kind: REAUTH_REQUIRED, message: extractErrorMessage(err, FRIENDLY_REAUTH_MESSAGE) };
  }
  if (status === 403) {
    // Microsoft auth succeeded but no CMS account exists for this identity; re-auth won't fix that.
    return { kind: NOT_AUTHORIZED, message: extractErrorMessage(err, FRIENDLY_NOT_AUTHORIZED_MESSAGE) };
  }
  // No response, a 5xx, or any non-InteractionRequiredAuthError MSAL error -- treated as transient.
  return { kind: TRANSIENT, message: extractErrorMessage(err, FRIENDLY_TRANSIENT_MESSAGE) };
}

let resetInFlight = false;

export const forceReLogin = async (message) => {
  
  if (resetInFlight) return;
  resetInFlight = true;
  // Dispatched first/synchronously so listeners (e.g. the proactive-refresh timer) can cancel before storage is cleared.
  window.dispatchEvent(new CustomEvent('tifoxr-force-relogin'));
  // Storage keys are cleared unconditionally even if persisting the message above fails.
  try {
    if (message) {
      sessionStorage.setItem(TIFOXR_AUTH_ERROR_KEY, message);
    } else {
      sessionStorage.removeItem(TIFOXR_AUTH_ERROR_KEY);
    }
  } catch (e) {
    console.error('Failed to persist auth failure message:', e);
  }
  try {
    ALL_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    // e.g. localStorage blocked by policy/extension -- must not abort the reset before navigation.
    console.error('Failed to clear app storage during forced re-login:', e);
  }
  try {
    // clearCache() has no built-in timeout, unlike acquireTokenSilent -- a hang would block navigation forever.
    await withTimeout(msalInstance.clearCache(), 10000, 'clearCache');
  } catch (e) {
    console.error('clearCache failed during forced re-login:', e);
  } finally {
    // Always navigate, even if the above threw/hung, so the user lands on a working /login.
    if (window.location.pathname === '/login') {
      // Already on /login -- force a full reload so stale React/MSAL state re-initializes.
      window.location.reload();
    } else {
      window.location.href = '/login';
    }
  }
};

const withTimeout = (promise, ms, label) => {
  let timeoutHandle;
  const timeout = new Promise((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutHandle));
};

// Single-attempt CIAM token exchange (private) -- exchangeCiamToken below is the guarded wrapper callers use.
const performCiamExchange = async (account) => {
  const result = await withTimeout(
    msalInstance.acquireTokenSilent({ ...loginRequest, account }),
    15000,
    'acquireTokenSilent',
  );

  const response = await axios.post(
    `${API_BASE}/api/auth/cms/login`,
    { authToken: result.idToken },
    { _isTokenExchange: true, _silent: true },
  );

  const { token, expiresAt, userId, name, email, systemRole, entityId, spaces } = response.data;

  localStorage.setItem(TIFOXR_TOKEN_KEY,        token);
  localStorage.setItem(TIFOXR_TOKEN_EXPIRY_KEY,  expiresAt);
  localStorage.setItem(TIFOXR_USER_ID_KEY,       userId ?? '');
  localStorage.setItem(TIFOXR_USER_NAME_KEY,     name ?? '');
  localStorage.setItem(TIFOXR_USER_EMAIL_KEY,    email ?? '');
  localStorage.setItem(TIFOXR_SPACES_KEY,        JSON.stringify(spaces ?? []));
  localStorage.setItem(TIFOXR_SYSTEM_ROLE_KEY,   systemRole ?? '');
  if (entityId != null) {
    localStorage.setItem(TIFOXR_ENTITY_ID_KEY, String(entityId));
  } else {
    localStorage.removeItem(TIFOXR_ENTITY_ID_KEY);
  }

  return token;
};

let exchangeInFlight = null; // { homeAccountId, promise } | null
let consecutiveTransientFailures = 0;

// Deliberately higher than AuthContext's own proactive-refresh retry budget (2), so this safety net fires later.
const MAX_CONSECUTIVE_TRANSIENT_FAILURES = 4;

function isCachedTokenExpiredOrMissing() {
  const expiry = localStorage.getItem(TIFOXR_TOKEN_EXPIRY_KEY);
  return !expiry || new Date(expiry) <= new Date();
}

// Dedupes concurrent calls per account (avoids MSAL's block_iframe_reload); escalates to re-login after repeated transient failures once the JWT expires.
export const exchangeCiamToken = (account) => {
  if (exchangeInFlight && exchangeInFlight.homeAccountId === account?.homeAccountId) {
    return exchangeInFlight.promise;
  }

  const promise = performCiamExchange(account)
    .then((token) => {
      consecutiveTransientFailures = 0;
      return token;
    })
    .catch((err) => {
      const failure = classifyAuthFailure(err);
      if (failure.kind === TRANSIENT) {
        consecutiveTransientFailures += 1;
        if (
          consecutiveTransientFailures >= MAX_CONSECUTIVE_TRANSIENT_FAILURES &&
          isCachedTokenExpiredOrMissing()
        ) {
          consecutiveTransientFailures = 0;
          forceReLogin(failure.message);
        }
      } else {
        consecutiveTransientFailures = 0;
      }
      throw err;
    })
    .finally(() => {
      exchangeInFlight = null;
    });

  exchangeInFlight = { homeAccountId: account?.homeAccountId, promise };
  return promise;
};

// Registered by ApprovalContext on mount; called by the interceptor on 422 (NoEligibleApproverException).
let _noApproverHandler = null;
export const setNoApproverCallback = (fn) => { _noApproverHandler = fn; };

let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

axios.defaults.timeout = 30000;

axios.interceptors.request.use((config) => {
  if (!config._silent) inc();
  const token = localStorage.getItem(TIFOXR_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

axios.interceptors.response.use(
  (res) => { if (!res.config._silent) dec(); return res; },
  async (err) => {
    if (!err.config?._silent) dec();
    const originalRequest = err.config;

    if (err.response?.status === 401 && originalRequest && !originalRequest._retry && !originalRequest._isTokenExchange) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        }).catch((e) => Promise.reject(e));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
          isRefreshing = false;
          processQueue(err, null);
          forceReLogin(FRIENDLY_REAUTH_MESSAGE);
          return Promise.reject(err);
        }
        const token = await exchangeCiamToken(accounts[0]);
        processQueue(null, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        const failure = classifyAuthFailure(refreshError);
        processQueue(refreshError, null);
        if (failure.kind === REAUTH_REQUIRED) {
          forceReLogin(failure.message);
        } else {
          // NOT_AUTHORIZED/TRANSIENT: reauth wouldn't help -- surface the message without wiping the session.
          window.dispatchEvent(new CustomEvent('api-error', { detail: { message: failure.message } }));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 422 usually means a plain validation failure — only divert to the dedicated modal when the
    // backend actually marked this one as NoEligibleApproverException (Kind field, or the older
    // { error: "no_eligible_approver" } shape from ContentApprovalController.Submit).
    if (err.response?.status === 422) {
      const data = err.response.data;
      const isNoApprover = data?.kind === 'no_eligible_approver' || data?.error === 'no_eligible_approver';
      if (isNoApprover) {
        _noApproverHandler?.(extractErrorMessage(err));
        return Promise.reject(err);
      }
    }

    // A request the caller aborted itself (a newer one superseded it) is not a failure to toast.
    if (err?.code === 'ERR_CANCELED') return Promise.reject(err);

    if (!originalRequest?._isTokenExchange) {
      window.dispatchEvent(new CustomEvent('api-error', { detail: { message: extractErrorMessage(err) } }));
    }
    return Promise.reject(err);
  },
);
